declare global {
    interface String {
        capitalize(): string;
        splitWords(): string[];
        isEqual(str: string | undefined): boolean;
        equalsAny(...strings: string[]): boolean;
        toSentence(): string;
    }
    interface Array<T> {
        sortBy(predicate: (element: T) => any): void;
        /**
         * Conditionally removes items from an array and returns a new array with the removed items.
         */
        removeWhere(predicate: (item: T) => boolean): T[];

        remove(item: T): T[];

        selectMany<TChild>(selector: (item: T) => TChild[]): TChild[];
        first(): T;
        last(): T;

        clear(): void;

        reorder(fromIndex: number, toIndex: number, undo?: boolean): void;

        contains(item: T): boolean;
        /**
         * Given a predicate to reduce each item in the array to a value,
         * will return that value if all items in the array have the same value.
         * If not, will return null.
         */
        getCommonOrNull(predicate: (item: T) => any): any;

        toMap<TKey>(keySelector: (item: T) => TKey): Map<TKey, T>;
    }
    interface Set<T> {
        find(predicate: (item: T) => boolean): T | undefined;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface Map<K, V> {
        hasAny(...keys: K[]): boolean;
    }
}

String.prototype.capitalize = function () {
    return this.charAt(0).toUpperCase() + this.slice(1);
};

const splitWordsRegex = /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g;
String.prototype.splitWords = function () {
    return this.match(splitWordsRegex) || [];
};

String.prototype.isEqual = function (str: string) {
    return (this ? this.toUpperCase() : undefined) === (str ? str.toUpperCase() : undefined);
};

String.prototype.equalsAny = function (...strings: string[]) {
    return strings && strings.some(s => s.isEqual(this as string));
};

String.prototype.toSentence = function () {
    return this.replace(/([A-Z])/g, ' $1') // insert a space before all caps
        .replace(/^./, function (str) {
            return str.toUpperCase();
        }) // uppercase the first character
        .trim(); //trip leading and trainling whitespace
};

Object.defineProperty(Array.prototype, 'sortBy', {
    enumerable: false,
    writable: true,
    value: function (predicate: (item: any) => any) {
        return (this as any[]).sort((a, b) => {
            if (predicate(a) < predicate(b)) {
                return -1;
            } else if (predicate(a) > predicate(b)) {
                return 1;
            } else {
                return 0;
            }
        });
    },
});

Object.defineProperty(Array.prototype, 'remove', {
    enumerable: false,
    writable: true,
    value: function (item: any): any[] | undefined {
        const index = this.indexOf(item);
        if (index != -1) {
            return this.splice(this.indexOf(item), 1);
        }
    },
});

Object.defineProperty(Array.prototype, 'removeWhere', {
    enumerable: false,
    writable: true,
    value: function (predicate: (item: any) => boolean) {
        const removed = new Array<any>();
        for (let i = this.length - 1; i >= 0; i--) {
            const item = this[i];
            if (predicate(item)) {
                removed.push(...this.splice(i, 1));
            }
        }
        return removed;
    },
});

Object.defineProperty(Array.prototype, 'contains', {
    enumerable: false,
    writable: true,
    value: function (item: any): boolean {
        const index = this.indexOf(item);
        return index != -1;
    },
});

Object.defineProperty(Array.prototype, 'selectMany', {
    enumerable: false,
    writable: true,
    value: function (selector: (item: any) => any[]) {
        return this.reduce((out: any, v: any) => {
            let vres = selector(v);
            if (vres != null) {
                if (!Array.isArray(vres)) vres = [vres];
                out.push(...vres);
            }

            return out;
        }, []);
    },
});

Object.defineProperty(Array.prototype, 'first', {
    enumerable: false,
    writable: true,
    value: function () {
        let item;
        if (this.length) {
            item = this[0];
        }
        return item;
    },
});

Object.defineProperty(Array.prototype, 'last', {
    enumerable: false,
    writable: true,
    value: function () {
        if (this.length) {
            return this[this.length - 1];
        }
        return undefined;
    },
});

Object.defineProperty(Array.prototype, 'getCommonOrNull', {
    enumerable: false,
    writable: true,
    value: function (valueDelegate: (item: any) => any) {
        const arr = this as any[];
        if (this.length && arr.every(i => valueDelegate(i) == valueDelegate(arr[0]))) {
            return valueDelegate(arr[0]);
        }
        return null;
    },
});

Object.defineProperty(Array.prototype, 'toMap', {
    enumerable: false,
    writable: true,
    value: function (keySelector: (item: any) => any) {
        const m = new Map();
        for (const i of this) {
            m.set(keySelector(i), i);
        }
        return m;
    },
});

Object.defineProperty(Array.prototype, 'clear', {
    enumerable: false,
    writable: true,
    value: function () {
        if (this.length > 0) {
            this.splice(0, this.length);
        }
    },
});

Object.defineProperty(Array.prototype, 'reorder', {
    enumerable: false,
    writable: true,
    value: function (fromIndex: number, toIndex: number, undo?: boolean) {
        if (undo) {
            if (fromIndex > toIndex) {
                fromIndex--;
            } else {
                toIndex++;
            }
        }
        const itemToMove = this[fromIndex];
        for (let i = fromIndex; i < this.length - 1; i++) {
            this[i] = this[i + 1];
        }
        if (toIndex > fromIndex) {
            toIndex--;
        }
        for (let i = this.length - 1; i > toIndex; i--) {
            this[i] = this[i - 1];
        }
        this[toIndex] = itemToMove;
    },
});

Object.defineProperty(Set.prototype, 'find', {
    enumerable: false,
    writable: true,
    value: function (predicate: (item: any) => boolean) {
        for (const i of this) {
            if (predicate(i)) return i;
        }
        return undefined;
    },
});

Object.defineProperty(Map.prototype, 'hasAny', {
    enumerable: false,
    writable: true,
    value: function (...keys: any[]) {
        return keys.some(k => this.has(k));
    },
});

export {};
